/**
 * A stable, anonymous id for this browser.
 *
 * Not a tracking profile: a random opaque string in localStorage, tied to
 * nothing and sent nowhere but our own API. It exists so the view counter can
 * dedupe. Without it, one reader refreshing ten times is ten views.
 *
 * Key and shape copied from `apps/frontend/src/utils/visitor.ts` on purpose.
 * localStorage is per-origin so the id is not literally shared with gazar.dev,
 * but the two surfaces write the same key in the same format, which is what
 * makes the counter comparable when the rows land in one table.
 *
 * Browser-only by definition. Returns null on the server rather than inventing
 * an id, which would differ on every render and dedupe nothing.
 */
const KEY = "gz:vid";

export function visitorId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.localStorage.setItem(KEY, id);
    return id;
  } catch {
    // Private mode, storage disabled, quota. An uncounted reader beats a throw
    // inside a page render.
    return null;
  }
}
